import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import type { StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { useManagedAccountAccessSessionState } from '../../hooks/useManagedAccountAccessSessionState';
import { Button } from '../../components-v2/Button';
import { getUserReferenceDisplayName, returnToManagementAccountSession } from '../../utils/managedAccountAccessSession';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	userSession: Api.UserSessionContext;
	fixed?: boolean;
}

function EyeBannerIcon(props: React.SVGProps<SVGSVGElement>) {
	return (
		<svg
			aria-hidden='true'
			fill='none'
			height={18}
			stroke='currentColor'
			strokeLinecap='round'
			strokeLinejoin='round'
			strokeWidth={2}
			viewBox='0 0 24 24'
			width={18}
			{...props}
		>
			<path d='M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z' />
			<circle cx={12} cy={12} r={3} />
		</svg>
	);
}

function ReturnIcon(props: React.SVGProps<SVGSVGElement>) {
	return (
		<svg
			aria-hidden='true'
			fill='none'
			height={14}
			stroke='currentColor'
			strokeLinecap='round'
			strokeLinejoin='round'
			strokeWidth={2}
			viewBox='0 0 24 24'
			width={14}
			{...props}
		>
			<line x1={19} x2={5} y1={12} y2={12} />
			<polyline points='12 19 5 12 12 5' />
		</svg>
	);
}

export const ManagedAccountAccessWarning = observer(function ManagedAccountAccessWarning({
	className = '',
	styles = [],
	userSession,
	fixed = true,
}: IProps) {
	const { isLoading, isManagedAccountAccess, session } = useManagedAccountAccessSessionState();
	const [isReturning, setIsReturning] = React.useState(false);

	if (isLoading || !isManagedAccountAccess) {
		return null;
	}

	const managedAccountName =
		session?.managedAccount.companyName || userSession.account?.companyName || 'managed account';
	const managedUserName = session?.managedUser.name || getUserReferenceDisplayName(userSession.user) || 'selected user';
	const parentAccountName = session?.parentAccount.companyName || 'parent account';

	const onReturnToManagementAccount = async () => {
		setIsReturning(true);
		try {
			await returnToManagementAccountSession(userSession);
		} catch {
			toast.error('Unable to return to the parent account. Please log in again.');
		} finally {
			setIsReturning(false);
		}
	};

	return (
		<div className={`${className} ${css(styleSheet.container, fixed && styleSheet.fixed, ...styles)}`}>
			<div className={css(styleSheet.messageGroup)}>
				<EyeBannerIcon className={css(styleSheet.icon)} />
				<div className={css(styleSheet.message)}>
					You&apos;re signed in as <strong className={css(styleSheet.strong)}>{managedUserName}</strong>
					<span className={css(styleSheet.separator)}>·</span>
					<span className={css(styleSheet.accountName)}>{managedAccountName}</span>
				</div>
			</div>
			<Button
				variant='ghost'
				size='sm'
				styles={[styleSheet.action]}
				isLoading={isReturning}
				onClick={onReturnToManagementAccount}
			>
				<ReturnIcon />
				Return to {parentAccountName}
			</Button>
		</div>
	);
});
